import { useEffect, useMemo, useState } from 'react';
import { useCanSeeWarehouses, usePermissionChecker } from '../../store/auth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import {
  Modal, Form, Input, InputNumber, DatePicker, Row, Col, Table, Alert, Divider, Typography, Tag, Select, Button, Space, message,
} from '../../ui';
import { ArrowLeftRight, ChevronRight, MapPin, Plus, Truck, Wand2 } from 'lucide-react';
import dayjs from 'dayjs';
import { request } from '../../api/client';
import { fmtQty, isRedundantSku } from '../../lib/format';
import { splitBoxLoose, displayBoxLoose } from '../../lib/packaging';
import { formatIstDate } from '../../lib/time';
import { PALETTE } from '../../lib/palette';
import { TransporterFormModal, type TransporterRecord } from '../inventory/TransporterFormModal';
import { PhoneInput } from '../../components/PhoneInput';
import { mobileRules } from '../../lib/validators';

type Props = {
  open: boolean;
  onClose: () => void;
  orderId: string;
};

type OrderItem = {
  id: string;
  productNameSnapshot: string;
  qty: string;
  unitPrice: string;
};

/* The lines list and its header share ONE column template so every card lines up under the labels.
   Widths mirror the table this replaced; `Ship now` stays the roomiest — box + loose inputs and
   their labels sit side by side in it. */
const LINE_GRID = 'grid grid-cols-[24px_minmax(170px,1fr)_74px_78px_82px_106px_200px_88px] items-center gap-x-2';

export function DispatchModal({ open, onClose, orderId }: Props) {
  const canSeeWarehouses = useCanSeeWarehouses();
  // Moving stock between warehouses is the planner's decision: the plan and the consolidate endpoints need
  // ORDER.DISPATCH.PLAN or ORDER.EDIT. A warehouse manager packing a split order was sent the plan request
  // anyway (403 twice) and shown a Consolidate button that could only fail.
  const hasPerm = usePermissionChecker();
  const canPlan = hasPerm('ORDER.DISPATCH.PLAN') || hasPerm('ORDER.EDIT');
  const qc = useQueryClient();
  const [form] = Form.useForm();
  const [qtys, setQtys] = useState<Record<string, number>>({});
  // Box-packed lines: the packer's boxes+loose entry (source of truth once touched); qtys stays the total.
  const [boxLoose, setBoxLoose] = useState<Record<string, { box: number; loose: number }>>({});
  const [warehouseId, setWarehouseId] = useState<string | undefined>();
  // orderItemId -> (batchId -> qty): the source batch(es) the packer allocates per line.
  const [alloc, setAlloc] = useState<Record<string, Record<string, number>>>({});
  // Lines the packer has deliberately folded shut (see shippingItemIds below).
  const [collapsedLines, setCollapsedLines] = useState<string[]>([]);
  // Quick-add ("＋ Add a new transporter" in the dropdown) — a carrier missing from the master must
  // never be the reason a packed lorry waits, so it is created from here and selected straight away.
  const [addTransporterOpen, setAddTransporterOpen] = useState(false);
  // Watched rather than read on submit: the Pack button stays disabled until a transporter is chosen,
  // which is the rule ("no transporter, no pack") made visible instead of only enforced on the server.
  const transporterId = Form.useWatch('transporterId', form);

  const { data: order } = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => request<any>({ url: `/orders/${orderId}` }),
    enabled: open && !!orderId,
  });

  const { data: pending } = useQuery({
    queryKey: ['order-pending', orderId],
    queryFn: () => request<Record<string, number>>({ url: `/orders/${orderId}/shipments/pending` }),
    enabled: open && !!orderId,
  });

  // Active-only by default — a retired transporter must not be selectable (the server refuses it too).
  const { data: transporters } = useQuery({
    queryKey: ['transporters', 'active'],
    queryFn: () => request<TransporterRecord[]>({ url: '/transporters' }),
    enabled: open,
  });

  const { data: warehouses } = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => request<any[]>({ url: '/warehouses/my' }),
    // Gated so a role without warehouse access doesn't fire a call it is refused — see useCanSeeWarehouses.
    enabled: canSeeWarehouses && open,
  });
  // Live stock per product per warehouse (keyed by warehouse code) to show what
  // each warehouse can fulfil before dispatching from it.
  const { data: catalog } = useQuery({
    queryKey: ['catalog-cached'],
    queryFn: () => request<any[]>({ url: '/catalog/with-stock' }),
    enabled: open,
  });

  const dispatchWarehouses = useMemo(() => (warehouses ?? []).filter((w: any) => w.type !== 'PRODUCTION'), [warehouses]);
  // A WAREHOUSE_MANAGER can't list /warehouses, so the lookup is empty for them — fall back to the code the
  // order detail resolves server-side (the order ships from its assigned warehouse). Keeps the In-warehouse
  // column, the short-stock highlight and the "not enough stock" alert working for the WHM too.
  const whCode = (dispatchWarehouses.find((w: any) => String(w.id) === String(warehouseId))?.code
    ?? (String(warehouseId) === String(order?.dispatchWarehouseId) ? (order?.dispatchWarehouseCode as string | undefined) : undefined)) as string | undefined;
  // Warehouse is assigned at approval and locked here — the WM/packer ships from the order's warehouse.
  const assignedWh = order?.dispatchWarehouseId ? dispatchWarehouses.find((w: any) => String(w.id) === String(order.dispatchWarehouseId)) : null;
  // productId → available qty in the chosen warehouse.
  const whStockByProduct = useMemo(() => {
    const m = new Map<string, number>();
    if (whCode) for (const c of catalog ?? []) m.set(String(c.productId), Number(c.stock?.warehouses?.[whCode] ?? 0));
    return m;
  }, [catalog, whCode]);
  // Per-product box packing (from the live catalog) so the packer can pick boxes + loose.
  const packagingByProduct = useMemo(() => {
    const m = new Map<string, { enabled: boolean; upb: number }>();
    for (const c of catalog ?? []) m.set(String(c.productId), { enabled: !!c.boxPackingEnabled && Number(c.unitsPerBox) > 0, upb: Number(c.unitsPerBox) || 0 });
    return m;
  }, [catalog]);
  // Current boxes+loose for a line: the packer's entry once touched, else derived from the total (= pending).
  const blFor = (id: string, upb: number): { box: number; loose: number } => {
    if (boxLoose[id]) return boxLoose[id];
    const s = splitBoxLoose(Number(qtys[id] ?? 0), upb);
    return { box: s.boxes, loose: s.loose };
  };
  const setBox = (id: string, upb: number, box: number) => {
    const cur = blFor(id, upb); const b = Math.max(0, Math.floor(box || 0));
    setBoxLoose((s) => ({ ...s, [id]: { box: b, loose: cur.loose } }));
    setQtys((m) => ({ ...m, [id]: b * upb + cur.loose }));
  };
  const setLoose = (id: string, upb: number, loose: number) => {
    const cur = blFor(id, upb); const lo = Math.max(0, loose || 0);
    setBoxLoose((s) => ({ ...s, [id]: { box: cur.box, loose: lo } }));
    setQtys((m) => ({ ...m, [id]: cur.box * upb + lo }));
  };

  useEffect(() => {
    if (open && pending) {
      const initial: Record<string, number> = {};
      for (const [k, v] of Object.entries(pending)) initial[k] = Number(v);
      setQtys(initial);
      setBoxLoose({});
      form.resetFields();
    }
  }, [open, pending, form]);

  // Default the warehouse to wherever the order already shipped from (partial
  // dispatch), else leave empty so the packer chooses.
  useEffect(() => {
    if (open) setWarehouseId(order?.dispatchWarehouseId ? String(order.dispatchWarehouseId) : undefined);
  }, [open, order?.dispatchWarehouseId]);
  // Batches are warehouse-specific — clear allocations whenever the warehouse changes.
  useEffect(() => { setAlloc({}); }, [warehouseId]);

  /*
   * The confirm has to name the act it is confirming. This one still said "Dispatch this shipment? …
   * the customer is told it is on the way / Yes, dispatch" — text left over from before pack and
   * dispatch were split. Everything else on the screen already says PACK (the title, the alert, the
   * "Pack N units" button, the "Packed — stock committed" toast), and the endpoint below only creates
   * a PACKED shipment; `notify.orderDispatched` fires on the separate dispatch call, so nobody was
   * told anything. Worse, under invoice-before-dispatch "on the way" is exactly what must NOT have
   * happened yet — the last prompt before a stock-committing action was describing the control it
   * exists to protect as already bypassed.
   */
  const send = useConfirmedMutation({ title: 'Pack this shipment?', content: 'Stock is committed out of the warehouse now. The goods stay here until they are dispatched.', okText: 'Yes, pack' }, {
    mutationFn: (vals: any) =>
      request({
        url: `/orders/${orderId}/shipments`,
        method: 'POST',
        data: {
          ...vals,
          warehouseId,
          expectedAt: vals.expectedAt ? vals.expectedAt.toISOString() : undefined,
          lines: Object.entries(qtys)
            .filter(([_, q]) => Number(q) > 0)
            .map(([orderItemId, qty]) => ({
              orderItemId, qty: Number(qty),
              allocations: Object.entries(alloc[orderItemId] ?? {})
                .filter(([, bq]) => Number(bq) > 0)
                .map(([batchId, bq]) => ({ batchId, qty: Number(bq) })),
            })),
        },
      }),
    onSuccess: () => {
      message.success('Packed — stock committed');
      qc.invalidateQueries({ queryKey: ['order', orderId] });
      qc.invalidateQueries({ queryKey: ['order-timeline', orderId] });
      qc.invalidateQueries({ queryKey: ['order-shipments', orderId] });
      qc.invalidateQueries({ queryKey: ['order-pending', orderId] });
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Pack failed'),
  });

  const totalThisDispatch = Object.values(qtys).reduce((s, v) => s + Number(v || 0), 0);
  const totalPending = Object.values(pending ?? {}).reduce((s, v) => s + Number(v || 0), 0);
  const items: OrderItem[] = order?.items ?? [];

  const allocatedFor = (itemId: string) => Object.values(alloc[itemId] ?? {}).reduce((s, q) => s + (Number(q) || 0), 0);
  /*
   * A line's batch allocator opens BY ITSELF as soon as the line has a quantity — that is the whole
   * point, you should not have to hunt for it. The chevron then had nothing to do, because this
   * controlled list overrode every click: it looked like a button and behaved like a label.
   *
   * So the toggle now works ON TOP of that: auto-open stays the default, and a line the packer has
   * deliberately collapsed stays collapsed until they open it again. With twenty lines on screen that
   * is the difference between a usable modal and a mile of scrolling.
   */
  const shippingItemIds = items.filter((it) => Number(qtys[it.id] ?? 0) > 0).map((it) => String(it.id));
  const visibleAllocRows = shippingItemIds.filter((id) => !collapsedLines.includes(id));
  // Every line being shipped must have its batch allocation sum exactly to its ship qty.
  const allBatchAllocated = items.every((it) => {
    const q = Number(qtys[it.id] ?? 0);
    return q <= 0 || Math.abs(allocatedFor(it.id) - q) < 1e-6;
  });
  // No line may pack more than its pending qty (a box+loose entry can push the total over — guard it).
  const withinPending = items.every((it) => Number(qtys[it.id] ?? 0) <= Number(pending?.[it.id] ?? 0) + 1e-6);

  // Total available across ALL warehouses, per product (to decide if consolidation can fulfil).
  const totalStockByProduct = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of catalog ?? []) {
      const whs = (c.stock?.warehouses ?? {}) as Record<string, any>;
      m.set(String(c.productId), Object.values(whs).reduce((s: number, v: any) => s + Number(v || 0), 0));
    }
    return m;
  }, [catalog]);

  // Per-product pending across the order's lines.
  const pendingByProduct = useMemo(() => {
    const m = new Map<string, number>();
    for (const it of items) {
      const pid = String((it as any).productId);
      m.set(pid, (m.get(pid) ?? 0) + Number(pending?.[it.id] ?? 0));
    }
    return m;
  }, [items, pending]);

  // Consolidation: the chosen warehouse can't cover the full pending of some line, BUT the
  // combined stock across warehouses can — so we can transfer stock in and ship as one.
  const consolidationNeeded = useMemo(() => {
    if (!whCode) return false;
    for (const [pid, need] of pendingByProduct) if (need > (whStockByProduct.get(pid) ?? 0) + 1e-6) return true;
    return false;
  }, [whCode, pendingByProduct, whStockByProduct]);
  const consolidationFeasible = useMemo(() => {
    if (!consolidationNeeded) return false;
    for (const [pid, need] of pendingByProduct) if (need > (totalStockByProduct.get(pid) ?? 0) + 1e-6) return false;
    return true;
  }, [consolidationNeeded, pendingByProduct, totalStockByProduct]);

  const { data: plan } = useQuery({
    queryKey: ['consolidation-plan', orderId, warehouseId],
    queryFn: () => request<any[]>({ url: `/orders/${orderId}/consolidation-plan`, params: { toWarehouseId: warehouseId } }),
    enabled: open && !!orderId && !!warehouseId && consolidationFeasible && canPlan,
  });

  const consolidate = useConfirmedMutation({ title: "Bring stock in from other warehouses?", content: "Transfers are raised to consolidate the order at the dispatching warehouse.", okText: "Yes, consolidate" }, {
    mutationFn: () => request({ url: `/orders/${orderId}/consolidate`, method: 'POST', data: { toWarehouseId: warehouseId } }),
    onSuccess: () => {
      message.success('Consolidating stock — order held until the transfers are received');
      qc.invalidateQueries({ queryKey: ['order', orderId] });
      qc.invalidateQueries({ queryKey: ['order-timeline', orderId] });
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Consolidation failed'),
  });

  // Aggregate ship-now per product, then flag any product short in the chosen warehouse.
  const shortLines = useMemo(() => {
    if (!whCode) return [] as string[];
    const want = new Map<string, number>();
    for (const it of items) {
      const q = Number(qtys[it.id] ?? 0);
      if (q > 0) want.set(String((it as any).productId), (want.get(String((it as any).productId)) ?? 0) + q);
    }
    const short: string[] = [];
    for (const [pid, q] of want) if (q > (whStockByProduct.get(pid) ?? 0) + 1e-6) short.push(pid);
    return short;
  }, [items, qtys, whCode, whStockByProduct]);
  const hasShortage = shortLines.length > 0;

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={order ? `Pick & pack — ${order.orderNo}` : 'Pick & pack'}
      width={980}
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={send.isPending}
            disabled={totalThisDispatch <= 0 || !warehouseId || !allBatchAllocated || !withinPending || !transporterId}
            onClick={() => form.submit()}>
            {`Pack ${fmtQty(totalThisDispatch)} unit${totalThisDispatch === 1 ? '' : 's'}`}
          </Button>
        </>
      }
    >
      <Alert
        type="info"
        style={{ marginBottom: 12 }}
        title="Pick the source batch(es) for each product — supports full or partial packs."
        description="Set the quantity per product, then choose the batch(es) to pick from (Auto FEFO fills the earliest-expiry batches). Stock leaves the warehouse and is committed now, at pack — the order moves to PACKED. Name the transporter below — the LR, vehicle and driver details can follow if you don’t have them yet. Dispatch the packed goods separately, after the Tally invoice."
      />

      {/* Where to send it — the packer arranges transport here, so surface the consignee up front. The
          order has no separate ship-to; delivery goes to the dealer's registered address. */}
      <Alert
        type={order?.retailerAddress ? 'info' : 'warning'}
        icon={<MapPin size={16} />}
        style={{ marginBottom: 12 }}
        /* The Alert title is already semibold, so the old <b> around the name double-bolded it.
           The label is de-emphasised instead — the tinted Alert background rules out text-muted,
           so opacity plays the same role here that colour does elsewhere. */
        title={<span><span className="opacity-75">Deliver to:</span> {order?.retailerName ?? (order?.retailerId ? `#${order.retailerId}` : '—')}{order?.retailerMobile ? <> · <a href={`tel:${order.retailerMobile}`}>{order.retailerMobile}</a></> : null}</span>}
        description={order?.retailerAddress
          ? (
            <Space size="middle" wrap>
              <span>{order.retailerAddress}</span>
              <a target="_blank" rel="noopener" href={order.retailerLat && order.retailerLng
                ? `https://www.google.com/maps?q=${order.retailerLat},${order.retailerLng}`
                : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(order.retailerAddress)}`}>View map</a>
            </Space>
          )
          : 'No delivery address on file for this dealer — confirm with sales before dispatching.'}
      />

      <Form form={form} layout="vertical" onFinish={(v) => send.mutate(v)}>
        <Form.Item label="Dispatch warehouse" required style={{ marginBottom: 12 }}
          validateStatus={!warehouseId ? 'warning' : undefined}
          help={!warehouseId && !order?.dispatchWarehouseId ? 'Pick the warehouse to ship from' : undefined}>
          {order?.dispatchWarehouseId ? (
            // Locked: the fulfillment warehouse was chosen at approval; the order ships from there.
            <Space wrap>
              <Tag tone="blue" style={{ fontSize: 13, padding: '2px 10px', margin: 0 }}>
                {assignedWh ? `${assignedWh.code} · ${assignedWh.name}` : (order?.dispatchWarehouseName ?? whCode ?? '—')}
              </Tag>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Assigned at approval — this order ships from here.</Typography.Text>
            </Space>
          ) : (
            <Select
              showSearch optionFilterProp="label" placeholder="Select warehouse to ship from"
              value={warehouseId} onChange={setWarehouseId} style={{ maxWidth: 380 }}
              options={dispatchWarehouses.map((w: any) => ({ value: String(w.id), label: `${w.code} · ${w.name}`, isMaster: !!w.isMaster }))}
            />
          )}
        </Form.Item>

        <Typography.Title level={5}>Products</Typography.Title>
        {/* Each product line is its own CARD, with a real gap to the next one and the batch panel it
            opens living INSIDE it. As interleaved table rows a line and its panel could not be told
            apart from the next product's — boxing them was not enough, they had to separate.
            Brief: docs/handoffs/2026-09-09-pick-pack-batch-panel-grouping.md */}
        <div className="overflow-x-auto pb-1">
          <div className="min-w-[880px]">
            <div className={`${LINE_GRID} px-3.5 pb-2 type-overline text-muted`}>
              <span />
              <span>Product</span>
              <span className="text-right">Ordered</span>
              <span className="text-right">Shipped</span>
              <span className="text-right">Pending</span>
              <span className="text-right leading-tight">
                {whCode ? <>In stock<span className="block normal-case tracking-normal text-subtle">{whCode}</span></> : 'In warehouse'}
              </span>
              <span className="text-right">Ship now</span>
              <span className="text-right">Batch</span>
            </div>

            <div className="space-y-3">
              {items.map((r: any, i: number) => {
                const shipNow = Number(qtys[r.id] ?? 0);
                const max = Number(pending?.[r.id] ?? 0);
                const canExpand = !!warehouseId && shipNow > 0;
                const open = canExpand && visibleAllocRows.includes(String(r.id));
                const avail = whCode ? (whStockByProduct.get(String(r.productId)) ?? 0) : Infinity;
                const pk = packagingByProduct.get(String(r.productId));
                const allocated = allocatedFor(r.id);
                const label = `${r.productNameSnapshot}${r.variantLabel ? ` ${r.variantLabel}` : ''}`;
                return (
                  <div key={String(r.id)}
                    className={`overflow-hidden rounded-card border bg-surface ${open ? 'border-brand-500/45' : 'border-line'}`}>
                    <div className={`${LINE_GRID} px-3.5 py-3`}>
                      <button type="button" aria-label={`${open ? 'Hide' : 'Show'} batches for ${label}`}
                        disabled={!canExpand}
                        onClick={() => setCollapsedLines((c) => (c.includes(String(r.id)) ? c.filter((x) => x !== String(r.id)) : [...c, String(r.id)]))}
                        className="grid size-6 place-items-center rounded-control text-subtle transition-colors hover:bg-surface-2 disabled:opacity-0">
                        <ChevronRight size={14} className={`transition-transform duration-150 ${open ? 'rotate-90' : ''}`} />
                      </button>

                      {/* Positional line number in a left gutter — the one mark that VARIES per line,
                          so each product is a landmark you can point at, and the expander names it. */}
                      <div className="flex min-w-0 items-start gap-2.5">
                        <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-brand-500/12 type-caption-strong text-brand-700 dark:text-brand-300">{i + 1}</span>
                        <div className="min-w-0">
                          <div className="type-body-medium text-content">{label}</div>
                          {r.sku && !isRedundantSku(r.sku, `${r.productNameSnapshot} ${r.variantLabel ?? ''}`, r.productNameSnapshot)
                            ? <div className="type-caption text-muted">{r.sku}</div> : null}
                        </div>
                      </div>

                      <div className="text-right tabular-nums text-content">{fmtQty(r.qty)}</div>
                      <div className="text-right tabular-nums text-muted">{fmtQty(Number(r.qty) - max)}</div>
                      <div className="text-right">{max > 0 ? <Tag tone="gold" style={{ margin: 0 }}>{fmtQty(max)}</Tag> : <Tag tone="green" style={{ margin: 0 }}>0</Tag>}</div>

                      <div className="text-right">
                        {!whCode ? <span className="text-subtle">—</span> : (
                          <>
                            <Tag tone={shipNow > avail ? 'red' : avail > 0 ? 'green' : 'neutral'} style={{ margin: 0 }}>{fmtQty(avail)}</Tag>
                            {pk?.enabled && <div className="type-caption text-muted">{displayBoxLoose(avail, pk.upb)}</div>}
                          </>
                        )}
                      </div>

                      <div className="text-right">
                        {pk?.enabled ? (() => {
                          const bl = blFor(r.id, pk.upb);
                          const over = shipNow > max + 1e-6;
                          return (
                            <div>
                              <Space wrap size={4} style={{ justifyContent: 'flex-end' }}>
                                <InputNumber size="small" min={0} precision={0} value={bl.box} disabled={max <= 0} onChange={(v) => setBox(r.id, pk.upb, Number(v ?? 0))} style={{ width: 54 }} />
                                <span className="type-caption text-subtle">box</span>
                                <InputNumber size="small" min={0} value={bl.loose} disabled={max <= 0} onChange={(v) => setLoose(r.id, pk.upb, Number(v ?? 0))} style={{ width: 54 }} />
                                <span className="type-caption text-subtle">loose</span>
                              </Space>
                              <div className="type-caption" style={{ color: over || shipNow > avail ? PALETTE.danger[600] : 'var(--gb-text-muted)' }}>
                                = {fmtQty(shipNow)} units{over ? ` · max ${fmtQty(max)}` : ` · ${pk.upb}/box`}
                              </div>
                            </div>
                          );
                        })() : (
                          <InputNumber
                            min={0} max={max} step={1}
                            value={qtys[r.id] ?? 0}
                            disabled={max <= 0}
                            status={shipNow > avail ? 'error' : undefined}
                            onChange={(v) => setQtys((m) => ({ ...m, [r.id]: Number(v ?? 0) }))}
                            style={{ width: '100%' }}
                          />
                        )}
                      </div>

                      <div className="text-right">
                        {shipNow <= 0 ? <span className="text-subtle">—</span> : (
                          <Tag tone={Math.abs(allocated - shipNow) < 1e-6 ? 'green' : 'gold'} style={{ margin: 0 }}>
                            {fmtQty(allocated)}/{fmtQty(shipNow)}
                          </Tag>
                        )}
                      </div>
                    </div>

                    {open && (
                      <div className="border-t border-line bg-surface-2/70 p-3">
                        <BatchAllocRow warehouseId={warehouseId!} item={r} qty={shipNow} lineNo={i + 1}
                          alloc={alloc[r.id] ?? {}}
                          onChange={(batchId, q) => setAlloc((st) => ({ ...st, [r.id]: { ...(st[r.id] ?? {}), [batchId]: q } }))}
                          onAuto={(m) => setAlloc((st) => ({ ...st, [r.id]: m }))} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: hasShortage ? 8 : 16 }}>
          Pending across all products: <b>{fmtQty(totalPending)}</b> · Shipping now: <b>{fmtQty(totalThisDispatch)}</b>
        </Typography.Paragraph>
        {hasShortage && !consolidationFeasible && (
          <Alert type="error" style={{ marginBottom: 16 }}
            title={`Not enough stock in ${whCode} for ${shortLines.length} product${shortLines.length === 1 ? '' : 's'}.`}
            description="Reduce the ship-now quantity or pick a warehouse that has stock. You can dispatch the rest later (partial shipment)." />
        )}
        {consolidationFeasible && warehouseId && (
          <Alert type="warning" style={{ marginBottom: 16 }}
            title={`Stock is split across warehouses — ${whCode} alone can't cover this order.`}
            description={!canPlan ? (
              <div>
                The combined stock across warehouses <b>does</b> cover it, but bringing stock in from another
                warehouse is the planner&apos;s call. Pack what {whCode} holds now and the rest stays on backorder —
                the order is already on the planner&apos;s shortfall queue.
              </div>
            ) : (
              <div>
                <div style={{ marginBottom: 8 }}>
                  The combined stock across warehouses <b>does</b> cover it. Consolidate into <b>{whCode}</b> — the
                  system transfers the shortfall in (stock goes in transit), the order is held until the transfers are
                  received, then you dispatch the full quantity as one shipment.
                </div>
                {(plan ?? []).map((p: any) => (
                  <div key={p.fromWarehouseId} style={{ fontSize: 13 }}>
                    • <b>{p.fromName}</b> → {whCode}: {p.lines.map((l: any) => `${l.productName ?? l.productId} ×${fmtQty(l.qty)}`).join(', ')}
                  </div>
                ))}
                {/* The label WRAPS inside the alert. On one line "Consolidate to WH-PUNE & hold order" is ~300px,
                    wider than the alert on a phone, so it pushed the whole dialog body into a sideways scroll;
                    truncated it would lose what the button does. Height follows the lines. */}
                <Button variant="primary" icon={<ArrowLeftRight size={16} />}
                  className="py-1.5 text-left [&>span]:whitespace-normal"
                  style={{ marginTop: 10, maxWidth: '100%', height: 'auto', minHeight: 36 }}
                  loading={consolidate.isPending} disabled={!(plan ?? []).length}
                  onClick={() => consolidate.mutate()}>
                  Consolidate to {whCode} &amp; hold order
                </Button>
              </div>
            )} />
        )}

        <Divider style={{ margin: '8px 0' }} />
        <Typography.Title level={5}>Logistics</Typography.Title>
        {/* Fixed 12/6/6 and 8/8/8 spans held on every screen, so below desktop the date-and-time field
            (it needs ~236px for "15 Sep 2026 05:30 PM") pushed the whole dialog body into a sideways
            scroll. The fields now stack on a phone and pair up on a tablet; desktop is unchanged. */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            {/*
              * MANDATORY, and picked from the transporter master rather than typed. Free text is what
              * left the same firm in the data under three spellings, so "who is carrying this" could
              * never be answered afterwards. The server enforces the same rule (TRANSPORTER_REQUIRED).
              */}
            <Form.Item
              name="transporterId"
              label="Carrier / transporter"
              required
              rules={[{ required: true, message: 'Choose the transporter carrying this shipment' }]}
            >
              <TransporterSelect list={transporters ?? []} onAddNew={() => setAddTransporterOpen(true)} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item name="lrNo" label="LR / docket no">
              <Input placeholder="LR number" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item name="vehicleNo" label="Vehicle no">
              <Input placeholder="MH-04-AB-1234" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} sm={12} lg={8}>
            <Form.Item name="driverName" label="Driver name">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Form.Item name="driverMobile" label="Driver mobile" rules={mobileRules(false)}>
              <PhoneInput placeholder="Driver's mobile" />
            </Form.Item>
          </Col>
          <Col xs={24} lg={8}>
            <Form.Item name="expectedAt" label="Expected delivery">
              <DatePicker showTime={{ use12Hours: true, format: 'hh:mm A' }} format="DD MMM YYYY hh:mm A" className="w-full" disabledDate={(d) => d.isBefore(dayjs().startOf('day'))} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>

      {/* Quick-add, opened from the dropdown. On save the new transporter is selected here, so the
          packer carries straight on instead of losing the pack they had already set up. */}
      <TransporterFormModal
        quick
        open={addTransporterOpen}
        onClose={() => setAddTransporterOpen(false)}
        onCreated={(t) => form.setFieldValue('transporterId', String(t.id))}
      />
    </Modal>
  );
}

/** The sentinel option value — picking it opens the quick-add form instead of selecting a carrier. */
const ADD_TRANSPORTER = '__add_transporter__';

/**
 * The carrier picker. Rendered inside a Form.Item, so `value` / `onChange` arrive from the form.
 *
 * The add option is pinned past the search filter on purpose: the moment you most need it is when you
 * have typed a name that is NOT in the list, which is exactly when a normal filter would hide it.
 */
function TransporterSelect({ id, value, onChange, list, onAddNew }: {
  // Form.Item's id — without it the "Carrier / transporter" label pointed at nothing (E2E 15 Sep).
  id?: string;
  value?: string;
  onChange?: (v: string) => void;
  list: TransporterRecord[];
  onAddNew: () => void;
}) {
  const options = [
    ...list.map((t) => ({
      value: String(t.id),
      label: t.name,
      meta: [t.city, t.contactPerson, t.mobile].filter(Boolean).join(' · '),
      haystack: [t.name, t.city, t.contactPerson, t.mobile].filter(Boolean).join(' ').toLowerCase(),
    })),
    { value: ADD_TRANSPORTER, label: 'Add a new transporter', meta: '', haystack: '' },
  ];
  return (
    <Select
      id={id}
      showSearch
      // `|| undefined`, not `?? undefined`: Form.Item injects '' for an unset field, and the Select
      // reads any non-null value as "something is selected" — which renders an EMPTY trigger with no
      // placeholder, i.e. a mandatory field that looks like it has already been filled in.
      value={value || undefined}
      placeholder="Search the transporter" aria-label="Search the transporter"
      notFoundContent="No transporter matches — add them below."
      onChange={(v) => { if (v === ADD_TRANSPORTER) onAddNew(); else onChange?.(String(v)); }}
      options={options}
      filterOption={(input, o: any) => o.value === ADD_TRANSPORTER || String(o.haystack ?? '').includes(input.toLowerCase())}
      optionRender={(o: any) => (o.value === ADD_TRANSPORTER ? (
        <span className="flex items-center gap-1.5 font-medium text-brand-700 dark:text-brand-300">
          <Plus size={14} /> Add a new transporter
        </span>
      ) : (
        <span className="flex items-center gap-2">
          <Truck size={14} className="shrink-0 text-subtle" />
          <span className="min-w-0">
            <span className="block">{o.label}</span>
            {o.meta && <span className="block text-xs text-muted">{o.meta}</span>}
          </span>
        </span>
      ))}
    />
  );
}

// Per-line source-batch allocator: the packer chooses which batch(es) at the dispatch warehouse
// cover the line's ship qty. Auto FEFO fills earliest-expiry batches first. Allocated qty must equal
// the ship qty before dispatch is enabled (enforced in the parent + re-validated server-side).
function BatchAllocRow({ warehouseId, item, qty, lineNo, alloc, onChange, onAuto }: {
  warehouseId: string; item: any; qty: number; lineNo: number;
  alloc: Record<string, number>; onChange: (batchId: string, qty: number) => void; onAuto: (m: Record<string, number>) => void;
}) {
  const { data: batches } = useQuery({
    queryKey: ['dispatch-source-batches', warehouseId, item.productId],
    queryFn: () => request<any[]>({ url: '/transfer-source-batches', params: { warehouseId, productId: item.productId } }),
    enabled: !!warehouseId && !!item.productId,
  });
  const list = batches ?? [];
  const allocated = Object.values(alloc).reduce((s, q) => s + (Number(q) || 0), 0);
  const autoFefo = () => {
    let remaining = qty;
    const next: Record<string, number> = {};
    for (const b of list) {
      if (remaining <= 1e-9) break;
      const take = Math.min(Number(b.available), remaining);
      if (take > 0) { next[String(b.batchId)] = take; remaining -= take; }
    }
    onAuto(next);
  };
  const done = Math.abs(allocated - qty) < 1e-6;
  return (
    // A recessed shelf INSIDE the line's card — no border of its own, or it would be a box inside a
    // box. Green in here means exactly one thing, "stock is being taken from this lot", so the
    // nested table's brand-tinted header is neutralised; otherwise the allocation colour would be
    // just more green in a field of green.
    <div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-1 pb-2">
        <span className="grid size-5 shrink-0 place-items-center rounded-full bg-brand-500/12 type-caption-strong text-brand-700 dark:text-brand-300">{lineNo}</span>
        <span className="type-body-strong text-content">
          {item.productNameSnapshot ?? 'This product'}{item.variantLabel ? ` ${item.variantLabel}` : ''} — pick from these batches
        </span>
        <span className="type-caption text-subtle">earliest expiry first (FEFO)</span>
        <div className="ml-auto flex items-center gap-2">
          {/* A tinted, icon-led action: the outline `default` button on a white card read as a label
              rather than something to press, and this is the control that does the work on this row. */}
          <Button size="sm" variant="ghost" icon={<Wand2 size={14} />} onClick={autoFefo} disabled={!list.length}
            className="border border-brand-200 dark:border-brand-700/60">
            Auto FEFO
          </Button>
          <Tag tone={done ? 'green' : 'gold'} style={{ margin: 0 }}>{fmtQty(allocated)} / {fmtQty(qty)}</Tag>
        </div>
      </div>
      {!list.length && (
        <Alert type="warning" style={{ marginBottom: 8 }}
          title="No saleable (unexpired) stock for this product here — reduce the qty, pick another warehouse, or consolidate first." />
      )}
      <Table size="small" pagination={false} rowKey="batchId" dataSource={list}
        className="[&_thead]:bg-surface-2 [&_thead]:bg-none"
        rowClassName={(b: any) => (Number(alloc[String(b.batchId)] ?? 0) > 0 ? 'bg-success-500/10' : '')}
        locale={{ emptyText: 'No saleable stock for this product in the selected warehouse' }}
        columns={[
          {
            title: 'Batch no.',
            dataIndex: 'batchNo',
            // A batch number is a CODE, not a name: mono, in a boxed chip, so two near-identical lots
            // (…-377 vs …-378) are told apart at a glance. The chip itself never changes colour —
            // identity stays identity; the ROW tint is what says a lot is being picked from.
            render: (v: any) => (
              <code className="inline-block rounded border border-line bg-surface-2 px-1.5 py-0.5 font-mono text-xs text-content">{v}</code>
            ),
          },
          { title: 'Expiry', dataIndex: 'expDate', width: 120, render: (v: any) => (v ? <span className="tabular-nums text-muted">{formatIstDate(v)}</span> : '—') },
          { title: 'Available', dataIndex: 'available', align: 'right' as const, width: 110, render: (v: any) => <span className="tabular-nums">{fmtQty(v)}</span> },
          {
            title: 'Allocate', align: 'right' as const, width: 140,
            render: (_: any, b: any) => (
              <InputNumber min={0} max={Number(b.available)} value={alloc[String(b.batchId)] ?? 0}
                style={{ width: 116, marginLeft: 'auto' }}
                onChange={(v) => onChange(String(b.batchId), Number(v ?? 0))} />
            ),
          },
        ]} />
    </div>
  );
}
